/**
 * Wandelt eine Sparkasse-Kontowecker-Mail in eine vorbereitete Buchung um.
 * Das genaue Mail-Format variiert je nach Kontowecker-Einstellung, daher parst
 * diese Logik defensiv (Betrag, Richtung, Empfaenger) und legt den vollstaendigen
 * Mail-Text als Rohtext bei, damit der Nutzer im Kontroll-Dialog korrigieren kann.
 * Reine Logik ohne Redux/React.
 */

import { TYP_AUSGABE, TYP_EINNAHME } from './konstanten';
import { kategorieFuerAusgabe } from './mccMapping';

// Schluesselwoerter, die auf eine Einnahme (Geldeingang) hindeuten.
const EINGANG = /gutschrift|zahlungseingang|geldeingang|eingang|lohn|gehalt|erstattung/i;
// Schluesselwoerter, die auf eine Kartenzahlung/Bargeld hindeuten (Zahlweg).
const KARTE = /karte|kartenzahlung|girocard|visa|debit/i;
const BAR = /bargeld|auszahlung|geldautomat/i;

/** Findet den ersten Euro-Betrag im Text (deutsches Format), inkl. Vorzeichen. */
const findeBetrag = (text) => {
  const treffer = text.match(/(-?\d{1,3}(?:\.\d{3})*,\d{2})\s*(?:EUR|€)/i);
  if (!treffer) return null;
  return Number(treffer[1].replace(/\./g, '').replace(',', '.'));
};

/** Sucht den Wert hinter einem Label (z. B. "Empfänger: ...") bis Zeilenende. */
const feldWert = (text, ...labels) => {
  for (const label of labels) {
    const treffer = text.match(new RegExp(`${label}\\s*:?\\s*(.+)`, 'i'));
    if (treffer && treffer[1].trim()) return treffer[1].trim();
  }
  return '';
};

/** Findet ein Datum (TT.MM.JJJJ) im Text und gibt es als ISO zurueck. */
const findeDatum = (text) => {
  const treffer = text.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  return treffer ? `${treffer[3]}-${treffer[2]}-${treffer[1]}` : '';
};

/** ISO-Tagesstempel (JJJJ-MM-TT) aus einem Zeitstempel der Mail. */
const isoTag = (zeitstempel) => {
  const d = zeitstempel ? new Date(zeitstempel) : new Date();
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

/** Leitet den Zahlweg aus dem Mail-Text ab. */
const zahlwegVon = (text) => {
  if (KARTE.test(text)) return 'Karte';
  if (BAR.test(text)) return 'Bar';
  return 'Überweisung';
};

/** Kategorie-Vorschlag anhand von Typ und Empfaenger-/Zwecktext. */
const kategorieVon = (typ, text, notiz) => {
  if (typ === TYP_EINNAHME) {
    if (/lohn|gehalt|bezuege|bezüge/i.test(text)) return 'Gehalt';
    if (/zins/i.test(text)) return 'Zinsen';
    return 'Sonstiges';
  }
  return kategorieFuerAusgabe(null, notiz || text);
};

/**
 * Parst eine einzelne Kontowecker-Mail. Kann kein Betrag erkannt werden, wird die
 * Buchung inaktiv vorgeschlagen (Betrag 0), damit der Nutzer sie manuell pruefen kann.
 */
export function parseKontoweckerMail(mail) {
  const text = (mail.text || '').replace(/\r/g, '').trim();
  const betragRoh = findeBetrag(text);
  const typ = EINGANG.test(text) && (betragRoh == null || betragRoh >= 0) ? TYP_EINNAHME : TYP_AUSGABE;
  const empfaenger = feldWert(text, 'Empfänger', 'Beguenstigter', 'Begünstigter', 'Auftraggeber', 'Name');
  const zweck = feldWert(text, 'Verwendungszweck', 'Buchungstext', 'Betreff');
  const notiz = empfaenger || zweck || mail.betreff || '';

  return {
    schluessel: mail.uid != null ? `kw:${mail.uid}` : `kw:${mail.messageId}`,
    importId: `kw:${mail.messageId || mail.uid}`,
    uid: mail.uid,
    typ,
    betrag: betragRoh != null ? Math.round(Math.abs(betragRoh) * 100) / 100 : 0,
    kategorie: kategorieVon(typ, text, notiz),
    zahlweg: zahlwegVon(text),
    datum: findeDatum(text) || isoTag(mail.empfangen),
    notiz,
    aktiv: betragRoh != null,
    rohText: text || '(kein Text)',
  };
}

/** Parst mehrere Kontowecker-Mails und sortiert die Buchungen nach Datum aufsteigend. */
export function parseKontoweckerMails(mails) {
  return (mails || [])
    .map(parseKontoweckerMail)
    .sort((a, b) => String(a.datum).localeCompare(String(b.datum)));
}
